import { Check, Clock, type LucideIcon, X } from "lucide-react";
import type { ReactNode } from "react";

interface ApprovalActionBoxProps {
  /** Header label; defaults to the request-approval wording. */
  title?: string;
  /** Optional supporting line under the header. */
  description?: string;
  approveLabel?: string;
  rejectLabel?: string;
  /** Header icon; defaults to a clock. */
  icon?: LucideIcon;
  onApprove: () => void;
  onReject: () => void;
  isLoading?: boolean;
  /** Disables Approve (Reject stays available) — e.g. a workspace the type
   *  requires hasn't been added to the request yet. */
  approveDisabled?: boolean;
  /** Shown in place of `description` while `approveDisabled` is true. */
  approveDisabledReason?: string;
  /** Rendered between the description and the actions — what approving will
   *  do, when there is something to show or decide before deciding. */
  children?: ReactNode;
}

/**
 * Amber "awaiting approval" box with outline Approve / Reject actions. Shared by
 * the workplace-request detail sidebar and the reservation approval panel so
 * both approval surfaces look and behave identically.
 */
export const ApprovalActionBox = ({
  title = "Awaiting your approval",
  description,
  approveLabel = "Approve",
  rejectLabel = "Reject",
  icon: Icon = Clock,
  onApprove,
  onReject,
  isLoading,
  approveDisabled,
  approveDisabledReason,
  children
}: ApprovalActionBoxProps) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
    <div className="flex items-center gap-2 font-mona font-semibold text-amber-800 text-sm">
      <Icon className="h-4 w-4" />
      {title}
    </div>
    {approveDisabled && approveDisabledReason ? (
      <p className="mt-1 font-inter text-amber-700 text-sm">
        {approveDisabledReason}
      </p>
    ) : description ? (
      <p className="mt-1 font-inter text-neutral-600 text-sm">{description}</p>
    ) : null}
    {children}
    <div className="mt-3 flex gap-2">
      <button
        type="button"
        onClick={onApprove}
        disabled={isLoading || approveDisabled}
        className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-green-600 bg-white px-3 py-2 font-inter font-medium text-green-700 text-sm hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Check className="h-4 w-4" />
        {approveLabel}
      </button>
      <button
        type="button"
        onClick={onReject}
        disabled={isLoading}
        className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-2 font-inter font-medium text-red-700 text-sm hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <X className="h-4 w-4" />
        {rejectLabel}
      </button>
    </div>
  </div>
);

interface ApprovalPendingNoticeProps {
  title?: string;
  description?: string;
  icon?: LucideIcon;
}

/**
 * Read-only counterpart of {@link ApprovalActionBox}, shown to the requester
 * (or any non-approver) while their request is awaiting approval.
 */
export const ApprovalPendingNotice = ({
  title = "Awaiting approval",
  description = "Your request is being reviewed and is awaiting approval.",
  icon: Icon = Clock
}: ApprovalPendingNoticeProps) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
    <div className="flex items-center gap-2 font-mona font-semibold text-amber-800 text-sm">
      <Icon className="h-4 w-4" />
      {title}
    </div>
    <p className="mt-1 font-inter text-neutral-600 text-sm">{description}</p>
  </div>
);
